// Default comment attached to every field edit until a real comment-entry UI exists.
export const DEFAULT_AUDIT_COMMENT = "Initial Review";

// The audit service's resourceType for every sizer loan-field audit call.
// Must match the literal "ONLINE_SIZER" path segment postInitialAudit posts
// to (/audit/ONLINE_SIZER/{loanId}) — that's the bucket the seed call writes
// under, so every other audit call has to query/write the same bucket or it
// always looks like the loan has no audit history.
export const AUDIT_RESOURCE_TYPE = "ONLINE_SIZER";

// Mirrors PROPERTY_LEVEL_SECTIONS in src/hooks/useLoanSizingHandler.tsx —
// sections that live under propertyDetails[] rather than at the top level.
export const AUDIT_PROPERTY_LEVEL_SECTIONS = [
  "propertyLocation",
  "propertyInformation",
  "propertyEconomics",
  "unitInformation",
] as const;

// Builds the dotted path used as the audit key for a field, e.g.
// "data.loanEconomics.originalLoanAmount" or
// "data.propertyDetails[1].propertyInformation.propertyType".
export const getAuditFieldPath = (
  section: string,
  fieldId: string,
  propertyId?: number,
  unitId?: number,
): string => {
  const isPropertyLevel = AUDIT_PROPERTY_LEVEL_SECTIONS.includes(
    section as (typeof AUDIT_PROPERTY_LEVEL_SECTIONS)[number],
  );

  if (isPropertyLevel && propertyId != null && propertyId >= 0) {
    const propertyPrefix = `data.propertyDetails[${propertyId}]`;
    if (section === "unitInformation" && unitId != null && unitId >= 0) {
      return `${propertyPrefix}.unitInformation[${unitId}].${fieldId}`;
    }
    return `${propertyPrefix}.${section}.${fieldId}`;
  }

  return `data.${section}.${fieldId}`;
};

// Recursively flattens the whole loan data object into { path: value } pairs
// using the same "data.<section>.<field>" / "data.propertyDetails[i].<...>"
// convention as getAuditFieldPath, for seeding audit history on first save.
export const flattenForAudit = (
  obj: any,
  prefix = "data",
): Record<string, any> => {
  const result: Record<string, any> = {};
  if (obj == null || typeof obj !== "object") {
    return result;
  }

  Object.entries(obj).forEach(([key, value]) => {
    if (value == null) return;
    const path = `${prefix}.${key}`;

    if (Array.isArray(value)) {
      value.forEach((item, index) => {
        const itemPath = `${path}[${index}]`;
        if (item != null && typeof item === "object") {
          Object.assign(result, flattenForAudit(item, itemPath));
        } else {
          result[itemPath] = item;
        }
      });
    } else if (typeof value === "object") {
      Object.assign(result, flattenForAudit(value, path));
    } else {
      result[path] = value;
    }
  });

  return result;
};

// Pulls the list of audited field paths for one resource out of the
// /audit/fields/fetch response, which keys each resource by
// "{resourceType}_{resourceId}" (e.g. "ONLINE_SIZER_{loanId}") and maps
// straight to { <fieldPath>: { comment } } — the field paths are the object's
// own keys, not a nested array.
//
// postInitialAudit seeds every field of the loan with no comment at all, so
// a field with only that seed row would show comment: null here — only a
// field that's actually been edited (every edit capture path always sends a
// comment) gets a truthy comment. Filtering on that is what keeps the audit
// icon off fields that were only ever seeded, never really edited.
export const extractAuditedFieldPaths = (
  data: any,
  resourceId: string,
): string[] => {
  if (!data) return [];

  const entry = data?.[`${AUDIT_RESOURCE_TYPE}_${resourceId}`];
  if (!entry || typeof entry !== "object") return [];

  return Object.keys(entry).filter((path) => !!entry[path]?.comment);
};
